import { useAtom } from "jotai";

import { type Side } from "@design-lab/shared";
import { useShowWeaveAiImageRefinement } from "@design-lab/shared/lib/config";

import { aiDesignStateAtom, MENU_IDS, type AiDesignState } from "../../model/state";
import { useAiImageGeneration } from "../../model/useAiImageGeneration";
import { useAiImageRefinement } from "../../model/useAiImageRefinement";
import { useAiImageSelection } from "../../model/useAiImageSelection";
import { AiGalleryView } from "./AiGalleryView";
import { SAMPLE_IMAGE_URLS } from "./AiImageGallery";
import { AiRefinementView } from "./AiRefinementView";

type AiDesignContentProps = {
  currentSide: Side;
};

export function AiDesignContent({ currentSide }: AiDesignContentProps) {
  const [designState, setDesignState] = useAtom(aiDesignStateAtom);
  const currentState = designState[currentSide];
  const shouldShowWeaveAiImageRefinement = useShowWeaveAiImageRefinement();

  // Helper function to update design state for current side
  const updateCurrentSideState = (updates: Partial<AiDesignState[Side]>) => {
    setDesignState(prev => ({
      ...prev,
      [currentSide]: {
        ...prev[currentSide],
        ...updates
      }
    }));
  };

  // Use custom hooks for different concerns
  const { handlePromptChange, handleGenerate } = useAiImageGeneration({
    currentSide,
    updateState: updateCurrentSideState
  });

  const {
    handleEditImage,
    handleCancelRefinement,
    handleRefinementPromptChange,
    handleRefineSubmit,
    handleUndo,
    handleRedo,
    canUndo,
    canRedo
  } = useAiImageRefinement({
    side: currentSide,
    currentState,
    updateState: updateCurrentSideState
  });

  const { handleImageSelect, activeSelection } = useAiImageSelection({
    currentSide,
    currentState,
    updateState: updateCurrentSideState
  });

  const hasActualGeneratedImages = currentState.images.some(
    image => !SAMPLE_IMAGE_URLS.includes(image.imageSrc)
  );

  const displaySelectedImageId =
    activeSelection.activeSource === MENU_IDS.AI_DESIGN ? activeSelection.selectedId : undefined;

  // Get refinement image if in refinement mode
  const refinementImage = currentState.isRefining
    ? currentState.images.find(img => img.id === currentState.refinementImageId)
    : undefined;

  // Refinement View
  if (currentState.isRefining && refinementImage) {
    return (
      <AiRefinementView
        refinementImage={refinementImage}
        refinementPrompt={currentState.refinementPrompt}
        isRefining={currentState.isGenerating}
        isSelected={displaySelectedImageId === refinementImage.id}
        error={currentState.refinementError}
        onPromptChange={handleRefinementPromptChange}
        onRefine={handleRefineSubmit}
        onCancel={handleCancelRefinement}
        onSelect={imageId => {
          handleImageSelect(imageId);
          handleCancelRefinement();
        }}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={handleUndo}
        onRedo={handleRedo}
      />
    );
  }

  // Gallery View
  const galleryProps = {
    prompt: currentState.prompt,
    images: currentState.images,
    isGenerating: currentState.isGenerating,
    selectedImageId: displaySelectedImageId,
    uploadingImageId: currentState.uploadingImageId,
    error: currentState.error,
    startTime: currentState.startTime,
    hasGeneratedImages: hasActualGeneratedImages,
    onPromptChange: handlePromptChange,
    onGenerate: handleGenerate,
    onImageSelect: handleImageSelect,
    ...(hasActualGeneratedImages &&
      shouldShowWeaveAiImageRefinement && { onImageEdit: handleEditImage })
  };

  return <AiGalleryView {...galleryProps} />;
}
